<template>
  <div class="module-page">
    <!-- 搜索工具条 -->
    <el-card shadow="never" class="toolbar-card">
      <div class="toolbar">
        <el-input
          v-model="keyword"
          placeholder="输入用户名搜索"
          clearable
          class="keyword-input"
          @keyup.enter="handleSearch"
          @clear="handleSearch"
        />
        <el-select v-model="userType" placeholder="全部类型" clearable class="type-select">
          <el-option label="管理员" value="ADMIN" />
          <el-option label="普通用户" value="USER" />
        </el-select>
        <el-button type="primary" :icon="Search" @click="handleSearch">查询</el-button>
        <el-button :icon="Refresh" @click="handleReset">重置</el-button>
        <div class="toolbar-right">
          <el-button type="success" :icon="Plus" @click="openDialog()">新增用户</el-button>
        </div>
      </div>
    </el-card>

    <!-- 用户列表 -->
    <el-card shadow="never" class="table-card">
      <template #header>
        <span class="table-title">用户管理</span>
        <span class="table-tip">仅管理员可维护用户账号</span>
      </template>
      <el-table :data="rows" v-loading="loading" border stripe>
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column prop="username" label="用户名" min-width="120" show-overflow-tooltip />
        <el-table-column prop="email" label="邮箱" min-width="180" show-overflow-tooltip />
        <el-table-column prop="userType" label="用户类型" width="110" align="center">
          <template #default="{ row }">
            <el-tag :type="row.userType === 'ADMIN' ? 'danger' : 'info'" effect="light">
              {{ row.userType === 'ADMIN' ? '管理员' : '普通用户' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="loginCount" label="登录次数" width="100" align="right" />
        <el-table-column prop="lastLoginTime" label="上次登录时间" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">{{ row.lastLoginTime || '-' }}</template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" min-width="160" show-overflow-tooltip />
        <el-table-column prop="updateTime" label="更新时间" min-width="160" show-overflow-tooltip />
        <el-table-column label="操作" width="140" align="center" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDialog(row)">编辑</el-button>
            <el-button link type="danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        v-model:current-page="pageNum"
        v-model:page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next, jumper"
        background
        @change="fetchData"
      />
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="form.id ? '编辑用户' : '新增用户'"
      width="420px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="form.username" placeholder="请输入用户名" maxlength="64" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="form.email" placeholder="请输入邮箱" maxlength="128" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="form.password"
            type="password"
            show-password
            :placeholder="form.id ? '留空表示不修改密码' : '请输入密码'"
            maxlength="32"
          />
        </el-form-item>
        <el-form-item label="用户类型" prop="userType">
          <el-radio-group v-model="form.userType">
            <el-radio label="USER">普通用户</el-radio>
            <el-radio label="ADMIN">管理员</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, Plus } from '@element-plus/icons-vue'
import { userApi } from '@/api'

const rows = ref([])
const total = ref(0)
const loading = ref(false)
const saving = ref(false)
const keyword = ref('')
const userType = ref('')
const pageNum = ref(1)
const pageSize = ref(10)

const dialogVisible = ref(false)
const formRef = ref()
const form = reactive({ id: null, username: '', email: '', password: '', userType: 'USER' })

const rules = {
  username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '邮箱格式不正确', trigger: 'blur' }
  ],
  password: [
    {
      validator: (rule, value, callback) => {
        // 新增时必填,编辑时留空表示不变更
        if (!form.id && !value) {
          callback(new Error('请输入密码'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ],
  userType: [{ required: true, message: '请选择用户类型', trigger: 'change' }]
}

async function fetchData() {
  loading.value = true
  try {
    const data = await userApi.page({
      username: keyword.value || undefined,
      userType: userType.value || undefined,
      pageNum: pageNum.value,
      pageSize: pageSize.value
    })
    rows.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  pageNum.value = 1
  fetchData()
}

function handleReset() {
  keyword.value = ''
  userType.value = ''
  pageNum.value = 1
  fetchData()
}

function openDialog(row) {
  form.id = row ? row.id : null
  form.username = row ? row.username : ''
  form.email = row ? row.email : ''
  form.password = ''
  form.userType = row ? row.userType : 'USER'
  dialogVisible.value = true
  // 清除上次校验残留
  formRef.value?.clearValidate()
}

async function handleSave() {
  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) return
  saving.value = true
  try {
    const payload = {
      id: form.id,
      username: form.username,
      email: form.email,
      password: form.password || undefined,
      userType: form.userType
    }
    if (form.id) {
      await userApi.update(payload)
    } else {
      await userApi.add(payload)
    }
    ElMessage.success('保存成功')
    dialogVisible.value = false
    fetchData()
  } finally {
    saving.value = false
  }
}

async function handleDelete(row) {
  // 取消确认时 ElMessageBox reject,直接中断流程
  const confirmed = await ElMessageBox.confirm(`确定删除用户「${row.username}」吗？`, '提示', {
    type: 'warning',
    confirmButtonText: '确定',
    cancelButtonText: '取消'
  }).catch(() => false)
  if (!confirmed) return
  await userApi.remove(row.id)
  ElMessage.success('删除成功')
  // 删除末页最后一条时回退一页
  if (rows.value.length === 1 && pageNum.value > 1) {
    pageNum.value -= 1
  }
  fetchData()
}

onMounted(fetchData)
</script>

<style scoped>
.module-page {
  min-height: 100%;
}
.toolbar-card {
  margin-bottom: 16px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.keyword-input {
  width: 220px;
}
.type-select {
  width: 130px;
}
.toolbar-right {
  margin-left: auto;
}
.table-title {
  font-weight: 600;
}
.table-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
}
.pagination {
  margin-top: 16px;
  justify-content: flex-end;
}
@media (max-width: 768px) {
  .toolbar-right {
    margin-left: 0;
  }
  .keyword-input {
    width: 100%;
  }
}
</style>
